import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, CalendarDays, MapPin, MoreHorizontal, Pencil, Users } from 'lucide-react'
import http from '../config/http'
import EventDetails from '../components/Event/EventDetails'
import EventTabsSection from '../components/Event/EventTabsSection'
import bannerImg from '../assets/images/eventImage.png'
import { useSettings } from '../contexts/SettingsContext'

const money = value => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 }).format(Number(value) || 0)
const date = value => value ? new Date(value).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Not set'

export default function EventDetail() {
  const { id } = useParams()
  const { settings } = useSettings()
  const [event, setEvent] = useState({})
  const [related, setRelated] = useState({ expenses: [], staff: [], incidents: [], payments: [], invoices: [], attachments: [] })
  const [customerOptions, setCustomerOptions] = useState([])
  const [loading, setLoading] = useState(true)
  const [editRequested, setEditRequested] = useState(0)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const [{ data: eventData }, { data: customers }] = await Promise.all([http.get(`/api/events/${id}`), http.get('/api/customers')])
        setEvent(eventData)
        setRelated(eventData.related || {})
        setCustomerOptions(customers)
      } catch (error) {
        console.error('Error loading event:', error)
      } finally { setLoading(false) }
    }
    if (id) load()
  }, [id])

  const handleEventUpdate = async (section, updatedData) => {
    const updatePayload = section === 'recap' ? updatedData : {
      customerId: updatedData.customer,
      time: updatedData.time,
      eventType: updatedData.eventType,
      venue: updatedData.venue,
      location: updatedData.venue,
      description: updatedData.description,
      customFields: updatedData.customFields
    }
    try {
      const { data } = await http.put(`/api/events/${id}`, { ...event, ...updatePayload })
      setEvent(previous => ({ ...previous, ...data, ...updatePayload }))
    } catch (error) {
      console.error(`Error updating event ${section}:`, error)
      alert(`Error updating ${section}. Please try again.`)
    }
  }

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><div className="h-9 w-9 animate-spin rounded-full border-4 border-purple-200 border-t-purple-700" /></div>

  const paid = Number(event.financialSummary?.totalPayments ?? event.paid ?? event.pagado) || 0
  const total = Number(event.amount) || 0
  const internalCost = Number(event.cost) || 0
  const balance = Math.max(0, total - paid)
  const grossProfit = total - internalCost
  const paidPercent = total > 0 ? Math.min(100, paid / total * 100) : 0
  const margin = total > 0 ? grossProfit / total * 100 : 0
  const customerName = event.customerId?.name || 'Customer not selected'
  const venue = event.venue || event.location || 'Venue not selected'

  return <div className="min-h-screen bg-[#f7f8fb] pb-8">
    <header className="flex h-16 items-center justify-between border-b border-slate-200 bg-white px-6">
      <div className="flex items-center gap-4"><Link to="/events" className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"><ArrowLeft className="h-4 w-4" /></Link><div className="flex items-center gap-3 text-sm"><span className="font-medium text-slate-700">Event Management</span><span className="text-slate-300">›</span><b className="text-slate-950">{event.name || 'Event'}</b></div></div>
    </header>

    <section className="relative h-[235px] overflow-hidden bg-slate-800">
      <img src={bannerImg} alt="Event venue" className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/75 via-slate-900/25 to-transparent" />
      <div className="absolute inset-0 flex items-center justify-between px-8 py-6 text-white">
        <div className="flex items-center gap-6"><div className="flex h-20 w-20 items-center justify-center rounded-full border-8 border-white/90 bg-amber-50 text-3xl shadow-lg">✨</div><div><div className="flex items-center gap-3"><h1 className="text-4xl font-semibold tracking-tight">{event.name || 'Unnamed Event'}</h1><span className="rounded-full bg-white/15 px-3 py-1 text-xs font-medium backdrop-blur">{event.status || 'Pending'}</span></div><div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/90"><span>{event.eventType || 'Event'}</span><span className="h-4 w-px bg-white/40" /><span className="inline-flex items-center gap-2"><CalendarDays className="h-4 w-4" />{date(event.date)}</span><span className="h-4 w-px bg-white/40" /><span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" />{venue}</span><span className="h-4 w-px bg-white/40" /><span className="inline-flex items-center gap-2"><Users className="h-4 w-4" />{event.guests || 0} guests</span></div><p className="mt-3 text-sm"><span className="text-white/65">Customer:</span> {customerName}</p></div></div>
        <div className="flex gap-3 self-start"><button type="button" className="flex h-11 w-11 items-center justify-center rounded-lg border border-white/50 bg-white/90 text-slate-700 shadow"><MoreHorizontal className="h-5 w-5" /></button><button type="button" onClick={() => setEditRequested(value => value + 1)} className="inline-flex h-11 items-center gap-2 rounded-lg bg-[#4c087c] px-5 text-sm font-semibold text-white shadow-lg hover:bg-[#5f0a98]"><Pencil className="h-4 w-4" />Update Event</button></div>
      </div>
    </section>

    <main className="mx-auto max-w-[1500px] px-6 pt-5">
      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[330px_minmax(0,1fr)]">
        <aside className="space-y-5 xl:sticky xl:top-5">
          <EventDetails compact event={event} customerOptions={customerOptions} onUpdate={handleEventUpdate} settings={settings} editRequested={editRequested} />
        </aside>

        <section className="min-w-0">
          <section className="mb-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <dl className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-[repeat(3,minmax(0,1fr))_minmax(220px,1.5fr)]">
              <div className="border-r border-slate-100 pr-4"><dt className="text-xs text-slate-500">Total amount</dt><dd className="mt-1 text-base font-semibold text-slate-950">{money(total)}</dd></div>
              <div className="border-r border-slate-100 pr-4"><dt className="text-xs text-slate-500">Paid</dt><dd className="mt-1 text-base font-semibold text-emerald-700">{money(paid)}</dd></div>
              <div className="border-r border-slate-100 pr-4"><dt className="text-xs text-slate-500">Balance / Due</dt><dd className="mt-1 text-base font-semibold text-slate-950">{money(balance)}</dd></div>
              <div className="col-span-2 flex items-center gap-3 sm:col-span-3 lg:col-span-1">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${paidPercent}%` }} /></div>
                <span className="shrink-0 text-xs font-medium text-slate-500">{paidPercent.toFixed(1)}% paid</span>
              </div>
            </dl>
          </section>

          <EventTabsSection eventId={id} event={event} profitability={{ revenue: total, internalCost, grossProfit, margin }} expensesData={related.expenses || []} staffData={related.staff || []} onStaffUpdate={staff => setRelated(previous => ({ ...previous, staff }))} incidentsData={related.incidents || []} paymentsData={related.payments || []} invoicesData={related.invoices || []} attachmentsData={related.attachments || []} />
        </section>
      </div>
    </main>
  </div>
}
